import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Trash2, Pencil, Plus } from "lucide-react";
import { api, apiError, fmtDate } from "@/lib/api";
import { StatusBadge, KindBadge } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { AbsenceForm } from "@/components/AbsenceForm";

const today = new Date().toISOString().slice(0, 10);

export default function AdminRequests() {
  const { pharmacy } = useAuth();
  const types = pharmacy?.absence_types || [];
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [operators, setOperators] = useState([]);
  const [newOp, setNewOp] = useState("");
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const [{ data }, o] = await Promise.all([api.get("/absences", { params: { scope: "all" } }), api.get("/operators")]);
      setList(data);
      setOperators(o.data.filter((x) => x.role === "operator"));
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const decide = async (id, decision) => {
    try {
      await api.put(`/absences/${id}/decision`, null, { params: { decision } });
      toast.success(decision === "approved" ? "Richiesta approvata" : "Richiesta rifiutata");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/absences/${id}`);
      toast.success("Richiesta eliminata");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const shown = filter === "all" ? list : list.filter((a) => a.status === filter);

  return (
    <div className="space-y-6" data-testid="admin-requests">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-head text-3xl sm:text-4xl font-extrabold">Richieste di assenza</h1>
          <p className="text-sm text-gray-600 mt-1">Approva le richieste inviate dagli operatori (anche da casa) o inserisci tu ferie, permessi e malattie.</p>
        </div>
        <button data-testid="admin-absence-add-toggle" onClick={() => setAdding(!adding)} className="h-11 px-4 rounded-lg bg-emerald-600 text-white text-sm font-semibold flex items-center gap-2 hover:bg-emerald-700">
          <Plus className="h-4 w-4" /> Inserisci assenza
        </button>
      </div>

      {adding && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 space-y-3" data-testid="admin-absence-add">
          <label className="text-xs uppercase tracking-wider text-gray-600">Operatore
            <select data-testid="admin-absence-operator" value={newOp} onChange={(e) => setNewOp(e.target.value)} className="mt-1 block h-10 rounded-lg border border-gray-200 px-3 text-sm bg-white min-w-[220px]">
              <option value="">Seleziona operatore</option>
              {operators.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </label>
          {newOp && <AbsenceForm key={newOp} types={types} operatorId={newOp} initial={{ start_date: today, end_date: today }} onSaved={() => { setAdding(false); load(); }} onCancel={() => setAdding(false)} testPrefix="admin-abs-new" />}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {[["proposal", "Proposte"], ["pending", "In attesa"], ["approved", "Approvate"], ["rejected", "Rifiutate"], ["all", "Tutte"]].map(([v, l]) => (
          <button
            key={v}
            data-testid={`filter-${v}`}
            onClick={() => setFilter(v)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === v ? "bg-emerald-600 text-white" : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
        {shown.length === 0 ? (
          <p data-testid="requests-empty" className="text-sm text-gray-600">Nessuna richiesta in questa categoria.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-gray-600">
                <th className="pb-3">Operatore</th><th className="pb-3">Tipo</th><th className="pb-3">Periodo</th>
                <th className="pb-3">Ore</th><th className="pb-3">Note</th><th className="pb-3">Stato</th><th className="pb-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((a) => (
                editing === a.id ? (
                  <tr key={a.id} data-testid={`request-edit-row-${a.id}`} className="border-t border-gray-100">
                    <td colSpan={7} className="py-3">
                      <p className="text-sm font-medium mb-2">{a.operator_name}</p>
                      <AbsenceForm types={types} operatorId={a.operator_id} initial={a} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} testPrefix={`admin-abs-edit-${a.id}`} />
                    </td>
                  </tr>
                ) : (
                <tr key={a.id} data-testid={`request-row-${a.id}`} className="border-t border-gray-100">
                  <td className="py-3 font-medium">{a.operator_name}</td>
                  <td className="py-3"><KindBadge type={types.find((t) => t.key === a.kind) || { key: a.kind }} /></td>
                  <td className="py-3">{fmtDate(a.start_date)} – {fmtDate(a.end_date)}</td>
                  <td className="py-3 font-mono-num text-gray-600">{a.hours ? `${a.hours}h` : "—"}</td>
                  <td className="py-3 text-gray-600">{a.reason || "—"}</td>
                  <td className="py-3"><StatusBadge status={a.status} /></td>
                  <td className="py-3">
                    <div className="flex justify-end gap-2">
                      {a.status !== "approved" && (
                        <button
                          data-testid={`approve-${a.id}`}
                          onClick={() => decide(a.id, "approved")}
                          className="h-9 px-3 rounded-lg bg-emerald-600 text-white text-xs font-semibold flex items-center gap-1 transition-colors hover:bg-emerald-700 active:scale-95"
                        >
                          <Check className="h-3.5 w-3.5" /> Approva
                        </button>
                      )}
                      {a.status !== "rejected" && (
                        <button
                          data-testid={`reject-${a.id}`}
                          onClick={() => decide(a.id, "rejected")}
                          className="h-9 px-3 rounded-lg border border-red-200 text-red-600 text-xs font-semibold flex items-center gap-1 transition-colors hover:bg-red-50 active:scale-95"
                        >
                          <X className="h-3.5 w-3.5" /> Rifiuta
                        </button>
                      )}
                      <button
                        data-testid={`edit-absence-${a.id}`}
                        onClick={() => setEditing(a.id)}
                        aria-label="Modifica richiesta"
                        className="h-9 px-3 rounded-lg border border-gray-200 text-gray-700 text-xs font-semibold flex items-center transition-colors hover:bg-gray-50"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        data-testid={`delete-absence-${a.id}`}
                        onClick={() => remove(a.id)}
                        aria-label="Elimina richiesta"
                        className="h-9 px-3 rounded-lg border border-gray-200 text-gray-600 text-xs font-semibold flex items-center transition-colors hover:bg-gray-50"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
                )
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
